/**
 * Platform Health — State Invariants (A7Y-2332, frozen to the structured read
 * by A7Y-3047; registry/attestation reconciliation moved server-side by
 * A7Y-2586).
 *
 * The latest structured result for every REGISTERED invariant — including one
 * with no attestation at all — the sweep's own violation history, and
 * liveness for the A7Y-2179 sweep itself (both a silent sweep and a sweep
 * that ran but never published its structured results).
 *
 * The banners are the load-bearing part. Every assertion on this page exists
 * because "nothing reported it" was the failure mode, and the page must never
 * render an all-clear over an absence: a sweep that silently stopped running, a
 * failed structured read, a sweep that published no results at all, and a
 * sweep that published late and incomplete each get their own banner rather
 * than falling through to green.
 */
import { PageHeader } from '@/components/PageHeader';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { NativeInvariantStatus } from '@/pages/platform-health/types';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, MinusCircle, RefreshCw, XCircle } from 'lucide-react';

const REFRESH_INTERVAL_MS = 2 * 60 * 1000;
const RUN_URL_BASE = 'https://github.com/ApptonomyInc/a7y/actions/runs';

function relative(iso: string | null): string {
  const then = iso == null ? Number.NaN : Date.parse(iso);
  if (Number.isNaN(then)) return 'unknown';
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/**
 * The row's display label, in precedence order. Every reason a row is not a
 * plain outcome gets its own word, so a reader never has to infer "why is
 * this row not green" from a blank field.
 */
function rowLabel(invariant: NativeInvariantStatus): string {
  if (invariant.unreadable) return 'unreadable';
  if (invariant.byDesign === 'migration_debt') return 'migration debt';
  if (invariant.byDesign === 'legacy_check_registry') return 'legacy check_run_log';
  if (!invariant.attested) return 'never attested';
  if (invariant.conflict) return 'conflict';
  if (invariant.stale) return 'stale';
  if (!invariant.registered) return 'newer than deployed registry';
  if (invariant.completeness === 'never_seen') return 'never seen';
  return invariant.outcome ?? 'no outcome';
}

/**
 * One structured LATTICE result (A7Y-2581), now over every REGISTERED
 * invariant — including the 9 (measured 2026-09-13) that carry no production
 * attestation at all, which the retired page-side verdict could not show
 * because it only ever iterated attestation rows.
 *
 * GREEN IS OPT-IN, not the fallthrough. A row renders green only on the exact
 * claim `pass` on `complete` evidence with no staleness, conflict or
 * unreadable payload; `fail` is red; a registered-but-unattested invariant
 * that is unattested BY DESIGN (a migration-debt marker or a legacy
 * check_run_log member) is grey, not amber — it is not a gap, it is a known,
 * separately-evidenced shape; everything else is amber. Same rule as the
 * banner below, so a green row can never sit under an amber one.
 */
function NativeInvariantRow({ invariant }: { invariant: NativeInvariantStatus }) {
  const failed = invariant.outcome === 'fail';
  const byDesign = invariant.byDesign !== null;
  const clean =
    invariant.attested &&
    !invariant.stale &&
    !invariant.conflict &&
    !invariant.unreadable &&
    invariant.outcome === 'pass' &&
    invariant.completeness === 'complete';
  const incomplete = invariant.completeness === 'partial';
  const label = rowLabel(invariant);
  return (
    <li
      data-testid="platform-health-native-invariant-row"
      data-row-state={label}
      className="flex items-start gap-3 border-b border-gray-200 dark:border-zinc-700 px-4 py-3 last:border-b-0"
    >
      {failed ? (
        <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
      ) : byDesign ? (
        <MinusCircle className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
      ) : clean ? (
        <CheckCircle2
          className="mt-0.5 h-4 w-4 shrink-0 text-green-600 dark:text-green-400"
          aria-hidden="true"
        />
      ) : (
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
      )}
      <div className="min-w-0 flex-1">
        <p className="font-mono text-sm text-gray-900 dark:text-white">{invariant.checkId}</p>
        <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">{invariant.summary}</p>
      </div>
      <div className="shrink-0 text-right text-xs text-gray-500 dark:text-gray-400">
        <div
          className={cn(
            'font-medium',
            failed && 'text-red-600 dark:text-red-400',
            !failed && byDesign && 'text-gray-400',
            !failed && !byDesign && !clean && 'text-amber-600 dark:text-amber-400'
          )}
        >
          {label}
        </div>
        {/* Stated explicitly: a result standing on incomplete evidence is not
            the same claim as a clean one, and the prose could not say so. */}
        <div>{incomplete ? 'evidence incomplete' : invariant.completeness}</div>
      </div>
    </li>
  );
}

export function StateInvariantsPage() {
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['platform-health', 'state-invariants'],
    queryFn: () => api.getPlatformHealthStateInvariants(),
    refetchInterval: REFRESH_INTERVAL_MS,
  });

  const payload = data?.data;
  const native = payload?.native ?? [];
  const verdict = payload?.verdict ?? null;
  const liveness = payload?.liveness ?? null;

  return (
    <div className="px-4 py-0">
      <PageHeader
        title="State Invariants"
        subtitle="Deterministic state-invariant sweep status and violation history"
      />

      <div className="mb-4 flex items-center justify-between">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {payload?.lastSweep
            ? `Last sweep ${relative(payload.lastSweep.ts)} · ${payload.lastSweep.violationCount} violation(s)`
            : 'Loading…'}
        </p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-zinc-600 px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-zinc-700"
        >
          <RefreshCw
            className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')}
            aria-hidden="true"
          />
          Refresh
        </button>
      </div>

      {isError ? (
        <div
          data-testid="platform-health-invariants-error"
          className="rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-4 text-sm text-red-800 dark:text-red-200"
        >
          Couldn&apos;t load sweep history: {(error as Error)?.message || 'unknown error'}
        </div>
      ) : null}

      {/* First and loudest: a sweep that stopped running would otherwise make
          every invariant below render as reassuringly clean. */}
      {liveness?.cadenceGap ? (
        <div
          data-testid="platform-health-invariants-cadence-gap"
          className="mb-4 flex items-start gap-2 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-800 dark:text-red-200"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            The sweep runs every 2h but has not reported
            {liveness.hoursSinceLastSweep != null
              ? ` in ${Math.round(liveness.hoursSinceLastSweep)}h`
              : ' at all in the loaded window'}
            . Treat everything below as unverified — a silent sweep looks identical to a clean one.
          </span>
        </div>
      ) : null}

      {/* The heartbeat ran, but its structured results trail it beyond one
          cadence — the false-empty shape a written-but-unpublished sweep
          produces. */}
      {liveness?.publicationStale ? (
        <div
          data-testid="platform-health-invariants-publication-stale"
          className="mb-4 flex items-start gap-2 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-800 dark:text-red-200"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            The sweep reported {relative(liveness.lastSweepAt)} but its structured results date
            from {relative(liveness.latestAttestedAt)}. The rows below are not the latest sweep.
          </span>
        </div>
      ) : null}

      {isLoading ? (
        <div className="rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-12 text-center text-sm text-gray-500 dark:text-gray-400">
          Loading sweep history…
        </div>
      ) : null}

      {/* The three non-green verdicts render regardless of the liveness
          banners above — each says something they do not. Only the all-clear
          is suppressed under a gap or staleness, exactly as before: a green
          panel under a silent or late sweep is the claim this page must never
          make. `failing` shows no banner at all; the red rows below are the
          signal. */}
      {verdict?.kind === 'unavailable' ? (
        <div
          data-testid="platform-health-invariants-native-unavailable"
          className="mb-4 flex items-start gap-2 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-800 dark:text-red-200"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            The structured invariant read failed. Nothing below reflects the latest sweep.
          </span>
        </div>
      ) : null}

      {verdict?.kind === 'absent' ? (
        <div
          data-testid="platform-health-invariants-native-absent"
          className="mb-4 flex items-start gap-2 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-3 text-sm text-amber-800 dark:text-amber-200"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
          <span>
            The sweep has published no structured invariant results for production. This is not an
            all-clear.
          </span>
        </div>
      ) : null}

      {verdict?.kind === 'degraded' ? (
        <div
          data-testid="platform-health-invariants-partial"
          className="mb-4 flex items-start gap-2 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-3 text-sm text-amber-800 dark:text-amber-200"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
          <span>
            No invariant failed in the latest sweep, but{' '}
            {verdict.counts.unevaluated > 0 ? `${verdict.counts.unevaluated} could not be evaluated` : null}
            {verdict.counts.unevaluated > 0 && verdict.counts.unattested > 0 ? ' and ' : null}
            {verdict.counts.unattested > 0
              ? `${verdict.counts.unattested} registered sweep invariant(s) have no attestation`
              : null}
            . This is not an all-clear.
          </span>
        </div>
      ) : null}

      {payload?.allClear && verdict?.kind === 'clean' ? (
        <div
          data-testid="platform-health-invariants-all-clear"
          className="mb-4 rounded-lg border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-950/40 p-8 text-center"
        >
          <CheckCircle2
            className="mx-auto h-8 w-8 text-green-600 dark:text-green-400"
            aria-hidden="true"
          />
          <p className="mt-3 text-sm font-medium text-green-900 dark:text-green-100">
            All {verdict.counts.evaluatedClean} sweep-attested invariants passed on complete
            evidence in the latest sweep.
            {verdict.counts.unattestedByDesign > 0
              ? ` ${verdict.counts.unattestedByDesign} registered invariant(s) are not sweep-attested by design.`
              : ''}
            {verdict.counts.newerThanRegistry > 0
              ? ` ${verdict.counts.newerThanRegistry} attested id(s) are newer than the deployed registry.`
              : ''}
          </p>
        </div>
      ) : null}

      {native.length > 0 ? (
        <section
          data-testid="platform-health-native-invariants"
          className="mb-4 overflow-hidden rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800"
        >
          <header className="border-b border-gray-200 dark:border-zinc-700 px-4 py-2.5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Registered invariants — latest structured result
            </h3>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              Every registered invariant ({payload?.registryVersion}) — the sweep&apos;s latest
              structured result, or the reason there is none.
            </p>
          </header>
          <ul>
            {native.map((invariant) => (
              <NativeInvariantRow key={invariant.checkId} invariant={invariant} />
            ))}
          </ul>
        </section>
      ) : null}

      {payload && payload.history.length > 0 ? (
        <section className="mt-4 overflow-hidden rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800">
          <header className="border-b border-gray-200 dark:border-zinc-700 px-4 py-2.5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Sweep history</h3>
            {/* The two numbers on this page have different denominators and
                nothing joins them, so each is labelled where it appears. */}
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              Counts are the sweep&apos;s own violation total — one per offending subject, so it
              exceeds the number of invariants listed above.
            </p>
          </header>
          <ul>
            {payload.history.slice(0, 25).map((run, idx) => (
              <li
                key={`${run.runId ?? 'run'}-${idx}`}
                className="flex items-center justify-between border-b border-gray-200 dark:border-zinc-700 px-4 py-2 text-xs last:border-b-0"
              >
                <span className="text-gray-600 dark:text-gray-300">{relative(run.ts)}</span>
                <span
                  className={cn(
                    'font-medium',
                    run.violationCount > 0
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-green-600 dark:text-green-400'
                  )}
                >
                  {run.violationCount} violation(s)
                </span>
                {run.runId ? (
                  <a
                    href={`${RUN_URL_BASE}/${run.runId}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 hover:underline dark:text-blue-400"
                  >
                    run
                  </a>
                ) : (
                  <span className="text-gray-400 dark:text-zinc-500">—</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
